/*
The `ShareWidget` class is used to create a share widget on a webpage. It has the following methods:
- `constructor(baseUrl, title, dates, location, language = 'hu')`: Initializes the share widget
		with the given parameters. It sets the page title, page URL, language captions, and tooltip elements.
- `mobileShare()`: Checks if the device supports the `navigator.share` API and shares the link using the API.
		If not supported, it logs a message to the console.
- `init()`: Sets up event listeners for the tooltip elements. If the device is not mobile, it populates
		the share tooltip with the share items.
- `getSharedData()`: Returns an object containing the page title and URL.
- `popupWindow(url)`: Opens a new window with the given URL.
- `copyLink()`: Copies the link to the clipboard and displays a message.
- `sendLink()`: Opens a new window with a mailto link.
- `fbShare()`: Opens a new window with a Facebook share link.
- `lnShare()`: Opens a new window with a LinkedIn share link.
- `googleCalendar()`: Opens a new window with a Google Calendar share link.
- `downloadIcs()`: Opens a new window with an iCalendar file download link.
- `populateTooltip(tooltip, items)`: Populates the tooltip with the given items.
 */
class ShareWidget {
	constructor(baseUrl, title, dates, location, language = 'hu') {
		this.pageTitle = document.title;
		if (!baseUrl.length) {
			this.pageUrl = document.location.href;
		} else {
			this.pageUrl = baseUrl;
		}
		const lang = {
			hu: {
				copiedLink: 'Link másolva a vágólapra!',
				copyLink: 'Link másolása',
				sendLink: 'Küldés e-mailen',
				fbShare: 'Megosztás Facebook-on',
				lnShare: 'Megosztás Linkedin-en',
				googleCalendar: 'Google Calendar',
				downloadIcs: 'iCalendar fájl (.ics) letöltése',
			},
			en: {
				copiedLink: 'Link copied to clipboard!',
				copyLink: 'Copy link',
				sendLink: 'Send link via email',
				fbShare: 'Share on Facebook',
				lnShare: 'Share on LinkedIn',
				googleCalendar: 'Google Calendar',
				downloadIcs: 'Download iCalendar file (.ics)',
			}
		};
		this.captions = lang[language] || lang['hu'];
		this.shareTooltip = document.querySelector('#share[popover="auto"]');
		this.calendarTooltip = document.querySelector('#calendar[popover="auto"]');
		this.shareItems = [
			{ caption: this.captions.copyLink, id: "copyLink", callback: "copyLink" },
			{ caption: this.captions.sendLink, id: "sendLink", callback: "sendLink" },
			{ caption: this.captions.fbShare, id: "fbShare", callback: "fbShare" },
			{ caption: this.captions.lnShare, id: "lnShare", callback: "lnShare" },
		];
		this.calendarItems = [
			{ caption: this.captions.googleCalendar, id: "googleCalendar", callback: "googleCalendar" },
			{ caption: this.captions.downloadIcs, id: "downloadIcs", callback: "downloadIcs" },
		];
		this.baseUrl = baseUrl;
		this.title = title;
		this.dates = dates;
		this.location = location;
		this.details = '';

		this.init();
	}

	mobileShare() {
		if (navigator.share) {
			const params = this.getSharedData();
			navigator
				.share({
					title: params.title.replace(/'/g, '&apos;'),
					url: params.url
				})
				.then(() => {
					console.log('Link megosztva');
				})
				.catch(console.error);
		} else {
			console.log('Mobile share is not supported');
		}
	}

	init() {
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		document.addEventListener('DOMContentLoaded', () => {
			document.querySelectorAll('[data-tooltipmode]').forEach(el => {
				el.addEventListener('click', () => {
					if (isMobile && el.dataset.tooltipmode === 'share') {
						this.mobileShare();
					} else {
						[this.shareTooltip, this.calendarTooltip].find(t => t.id === el.dataset.tooltipmode).showPopover({ source: el, anchor: 'top-center' });
					}
				});
			});
			if (!isMobile) {
				this.populateTooltip(this.shareTooltip, this.shareItems);
			}
			this.populateTooltip(this.calendarTooltip, this.calendarItems);
		});
	}

	getSharedData() {
		return {
			title: this.pageTitle,
			url: this.pageUrl,
		};
	}

	popupWindow(url) {
		window.open(url, 'popUpWindow', 'height=600,width=600,left=100,top=100,resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no,status=no');
	}

	copyLink() {
		const params = this.getSharedData();
		navigator.clipboard.writeText(params.url);
		alert(this.captions.copiedLink);
		[this.shareTooltip, this.calendarTooltip].forEach(tooltip => {
			tooltip.hidePopover();
		});
	}

	sendLink() {
		const params = this.getSharedData();
		console.log(params);
		window.open('mailto:?subject=' + params.title + '&body=' + params.url);
	}

	fbShare() {
		const params = this.getSharedData();
		this.popupWindow('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(params.url));
	}

	lnShare() {
		const params = this.getSharedData();
		this.popupWindow('https://www.linkedin.com/shareArticle?mini=true&url=' + encodeURIComponent(params.url));
	}

	googleCalendar() {
		var url = 'https://www.google.com/calendar/render?action=TEMPLATE&text=' + this.title + '&dates=' + this.dates + '&details=' + this.details + '&location=' + this.location;
		this.popupWindow(url);
	}

	downloadIcs() {
		var url = this.baseUrl;
		this.popupWindow(url + '/iCal');
	}

	populateTooltip(tooltip, items) {
		if (tooltip) {
			const ul = tooltip.querySelector('ul');
			if (ul) {
				items.forEach(item => {
					ul.insertAdjacentHTML('beforeend', `<li><span id="${item.id}" data-callback="${item.callback}" class="pointer">${item.caption}</span></li>`);
					ul.querySelector(`#${item.id}`).addEventListener('click', (e) => {
						e.preventDefault();
						const callback = e.target.dataset.callback;
						if (typeof this[callback] === 'function') {
							this[callback]();
						}
					});
				});
			}
		}
	}
}
window.ShareWidget = ShareWidget;
